/**
 * HistoryScreen (S6) — read-only "Mening davomatim". Stays MOUNTED (stable key
 * "history" in App) while the S7 DaySheet overlays it, so its fetched month
 * persists across the overlay.
 * Props: { ctx, api, tg, nav, state, setState, params } (see App.jsx).
 *
 *   - api.summary()  → 3 Tiles (week / month / workdays) + a live line when
 *     summary.live_check_in (Tiles live={{hm:today_hm}}). Fetched once.
 *   - api.history(year,month) → <Calendar> (Monday-first, colour+glyph per day).
 *       canPrev  = min_month && current > min_month
 *       canNext  = !is_current_month
 *       onDayTap = (day) => nav.push(DAY,{day})
 *       todayDay = is_current_month ? today's date : null
 *   - Empty month (no present/open days) → the .empty state (tiles + pager stay).
 *   - Hours arrive PRE-FORMATTED ('8s 45m') — render verbatim. Own data only.
 */
import { useCallback, useEffect, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Spinner from '../components/Spinner.jsx';
import ErrorState from '../components/ErrorState.jsx';
import Tiles from '../components/Tiles.jsx';
import Calendar from '../components/Calendar.jsx';
import { T } from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';

const pad2 = (n) => String(n).padStart(2, '0');
const monthKey = (y, m) => `${y}-${pad2(m)}`;

export default function HistoryScreen({ api, nav }) {
  const [summary, setSummary] = useState(null);
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Summary is "this week / this month" — current, independent of the viewed
  // calendar month, so fetch it once.
  useEffect(() => {
    let alive = true;
    api.summary().then((res) => {
      if (alive && res && res.ok) setSummary(res);
    });
    return () => {
      alive = false;
    };
  }, [api]);

  const fetchMonth = useCallback(
    async (year, month) => {
      setLoading(true);
      setErr(null);
      const res = await api.history(year, month);
      if (res && res.ok) {
        setHist(res);
      } else {
        setErr((res && res.error) || 'SERVER_ERROR');
      }
      setLoading(false);
    },
    [api],
  );

  useEffect(() => {
    fetchMonth(undefined, undefined); // server returns the current month
  }, [fetchMonth]);

  function goPrev() {
    if (!hist) return;
    let y = hist.year;
    let m = hist.month - 1;
    if (m < 1) { m = 12; y -= 1; }
    fetchMonth(y, m);
  }

  function goNext() {
    if (!hist) return;
    let y = hist.year;
    let m = hist.month + 1;
    if (m > 12) { m = 1; y += 1; }
    fetchMonth(y, m);
  }

  // First load — nothing to show yet.
  if (loading && !hist) {
    return (
      <Screen title={T.title_history}>
        <Spinner size="lg" label={T.loading} />
      </Screen>
    );
  }

  if (err && !hist) {
    return (
      <Screen title={T.title_history}>
        <ErrorState code={err} onRecover={() => fetchMonth(undefined, undefined)} recover="retry" />
      </Screen>
    );
  }

  const tileItems = summary
    ? [
        { value: summary.week_hm, label: T.tile_week },
        { value: summary.month_hm, label: T.tile_month },
        { value: `${summary.month_days} ${T.unit_days}`, label: T.tile_workdays },
      ]
    : [];
  const live = summary && summary.live_check_in ? { hm: summary.today_hm } : null;

  const canPrev = !!hist.min_month && monthKey(hist.year, hist.month) > hist.min_month;
  const canNext = !hist.is_current_month;
  const todayDay = hist.is_current_month ? new Date().getDate() : null;
  const days = hist.days || [];
  const hasData = days.some((d) => d.status === 'present' || d.status === 'open');

  return (
    <Screen title={T.title_history} bodyStyle={{ gap: 9 }}>
      {tileItems.length ? <Tiles items={tileItems} live={live} /> : null}

      {hasData ? (
        <Calendar
          days={days}
          monthLabel={hist.month_label}
          onPrev={goPrev}
          onNext={goNext}
          canPrev={canPrev}
          canNext={canNext}
          onDayTap={(day) => nav.push(SCREENS.DAY, { day })}
          todayDay={todayDay}
        />
      ) : (
        <>
          <div className="pager">
            <button
              className={`ar ${canPrev ? '' : 'off'}`}
              onClick={canPrev ? goPrev : undefined}
              aria-label="Oldingi oy"
            >
              ‹
            </button>
            <span className="m">{hist.month_label}</span>
            <button
              className={`ar ${canNext ? '' : 'off'}`}
              onClick={canNext ? goNext : undefined}
              aria-label="Keyingi oy"
            >
              ›
            </button>
          </div>
          <div className="empty">
            <div className="ic">🗓️</div>
            <div className="h">{T.empty_month_h}</div>
            <div className="b">{T.empty_month_b}</div>
          </div>
        </>
      )}
    </Screen>
  );
}
